import { create } from "zustand";
import {
  getMmkItems,
  getPersonalCards,
  postMmkItemAction,
  savePersonalCards,
} from "../axios/mmk";
import { MmkItem } from "../types/mmk";
import {
  MAX_PERSONAL_MMK_CARDS,
  type PersonalMmkFormPayload,
} from "../helpers/mmkPersonal";
import useAppStore from "./appStore";
import { loadQuizState, loadFocusSessionState } from "../utils/mmkPersistence";
import {
  mmkItemFromPersonalCardApi,
  personalMmkItemsToSavePayload,
} from "../utils/mmkPersonalMapping";

function getHttpStatus(e: unknown): number | undefined {
  if (typeof e !== "object" || e === null || !("response" in e)) return undefined;
  const s = (e as { response?: { status?: number } }).response?.status;
  return typeof s === "number" ? s : undefined;
}

const MMK_ORDER_KEY_PREFIX = "mmk_items_order_";
const MMK_QUIZ_WRONG_KEY_PREFIX = "mmk_quiz_wrong_";
const MMK_MASTERY_CACHE_KEY_PREFIX = "mmk_mastery_cache_";

function getScopeKey(): string | null {
  const { focus, domain } = useAppStore.getState();
  if (!focus || !domain) return null;
  return `${focus}::${domain}`;
}

function getOrderKey(): string | null {
  const scope = getScopeKey();
  return scope ? `${MMK_ORDER_KEY_PREFIX}${scope}` : null;
}

function getQuizWrongKey(): string | null {
  const scope = getScopeKey();
  return scope ? `${MMK_QUIZ_WRONG_KEY_PREFIX}${scope}` : null;
}

function getMasteryCacheKey(): string | null {
  const scope = getScopeKey();
  return scope ? `${MMK_MASTERY_CACHE_KEY_PREFIX}${scope}` : null;
}

function loadMasteryCache(): { masteredCount: number; totalCount: number } | null {
  const key = getMasteryCacheKey();
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { masteredCount?: unknown; totalCount?: unknown };
    const mastered = Number(parsed?.masteredCount);
    const total = Number(parsed?.totalCount);
    if (!Number.isFinite(mastered) || !Number.isFinite(total) || total < 0) return null;
    return { masteredCount: Math.max(0, mastered), totalCount: Math.max(0, total) };
  } catch {
    return null;
  }
}

function saveMasteryCache(masteredCount: number, totalCount: number): void {
  const key = getMasteryCacheKey();
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify({ masteredCount, totalCount }));
  } catch {
    // ignore
  }
}

function loadQuizWrongIds(): Set<string> {
  const key = getQuizWrongKey();
  if (!key) return new Set();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    const arr = Array.isArray(parsed) ? parsed : [];
    return new Set(arr.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function addQuizWrongId(id: string): void {
  const key = getQuizWrongKey();
  if (!key) return;
  const ids = loadQuizWrongIds();
  ids.add(id);
  try {
    localStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

function removeQuizWrongId(id: string): void {
  const key = getQuizWrongKey();
  if (!key) return;
  const ids = loadQuizWrongIds();
  ids.delete(id);
  try {
    localStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

function loadSavedOrder(): string[] | null {
  const key = getOrderKey();
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string")
      : null;
  } catch {
    return null;
  }
}

function saveOrder(ids: string[]): void {
  const key = getOrderKey();
  if (!key || ids.length === 0) return;
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

function replaceSavedOrderId(oldId: string, newId: string): void {
  const key = getOrderKey();
  if (!key) return;
  const order = loadSavedOrder();
  if (!order) return;
  const next = order.map((id) => (id === oldId ? newId : id));
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function removeIdFromSavedOrder(id: string): void {
  const key = getOrderKey();
  if (!key) return;
  const order = loadSavedOrder();
  if (!order) return;
  const next = order.filter((x) => x !== id);
  try {
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function applySavedOrder<T extends { id: string }>(
  items: T[],
  savedOrder: string[],
): T[] {
  const byId = new Map(items.map((i) => [i.id, i]));
  const ordered: T[] = [];
  for (const id of savedOrder) {
    const item = byId.get(id);
    if (item) {
      ordered.push(item);
      byId.delete(id);
    }
  }
  // Append any new items not in saved order
  ordered.push(...byId.values());
  return ordered;
}

const normalizeMmkItem = (item: MmkItem): MmkItem => {
  const status =
    item.status ??
    (item.is_mastered
      ? "known"
      : item.is_pinned
        ? "pinned"
        : item.is_review_later
          ? "review_later"
          : "default");

  const is_mastered = item.is_mastered ?? status === "known";
  const is_pinned = item.is_pinned ?? status === "pinned";
  const is_review_later = item.is_review_later ?? status === "review_later";

  return {
    ...item,
    source: item.source ?? "catalog",
    status,
    is_mastered,
    is_pinned,
    is_review_later,
    category: item.category ?? null,
    question: item.question ?? item.title,
    approved_answer: item.approved_answer ?? item.content,
  };
};

interface MmkStore {
  items: MmkItem[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  lastKnownMasteredCount: number;
  lastKnownTotalCount: number;
  isQuizInProgress: boolean;
  setQuizInProgress: (value: boolean) => void;
  isFocusSessionInProgress: boolean;
  setFocusSessionInProgress: (value: boolean) => void;
  fetchItems: () => Promise<void>;
  clearStore: () => void;
  setItems: (items: MmkItem[]) => void;
  setTotalCount: (n: number) => void;
  updateItem: (id: string, updates: Partial<MmkItem>) => void;
  addPersonalMmkItem: (
    payload: PersonalMmkFormPayload,
  ) => Promise<
    | { ok: true }
    | { ok: false; error: "limit" | "no_scope" | "save_failed" }
  >;
  editPersonalMmkItem: (
    id: string,
    payload: PersonalMmkFormPayload,
  ) => Promise<
    | { ok: true; newId: string }
    | { ok: false; error: "not_found" | "save_failed" }
  >;
  deletePersonalMmkItem: (
    id: string,
  ) => Promise<
    { ok: true } | { ok: false; error: "not_found" | "save_failed" }
  >;
  addQuizWrongOverride: (id: string) => void;
  removeQuizWrongOverride: (id: string) => void;
}

let fetchItemsGeneration = 0;

function getInitialFocusSessionInProgress(): boolean {
  const scope = getScopeKey();
  return !!(
    scope &&
    !loadQuizState(scope) &&
    loadFocusSessionState(scope)?.sessionItemIds?.length
  );
}

export const useMmkStore = create<MmkStore>((set, get) => ({
  items: [],
  isQuizInProgress: false,
  setQuizInProgress: (value) => set({ isQuizInProgress: value }),
  isFocusSessionInProgress: getInitialFocusSessionInProgress(),
  setFocusSessionInProgress: (value) => set({ isFocusSessionInProgress: value }),
  totalCount: 15,
  loading: true,
  error: null,
  lastKnownMasteredCount: 0,
  lastKnownTotalCount: 15,

  fetchItems: async () => {
    const scope = getScopeKey();
    if (!scope) {
      // Avoid /mkk/items without focus/domain headers (400). Caller retries when ready.
      return;
    }
    const generation = ++fetchItemsGeneration;
    if (
      !loadQuizState(scope) &&
      loadFocusSessionState(scope)?.sessionItemIds?.length
    ) {
      set({ isFocusSessionInProgress: true });
    }
    const cache = loadMasteryCache();
    set({
      loading: true,
      error: null,
      ...(cache && {
        lastKnownMasteredCount: cache.masteredCount,
        lastKnownTotalCount: cache.totalCount,
      }),
    });
    try {
      const [{ items: data }, personalRes] = await Promise.all([
        getMmkItems(),
        getPersonalCards(),
      ]);
      if (generation !== fetchItemsGeneration) return;
      let normalizedCatalog = data.map(normalizeMmkItem);
      const quizWrongIds = loadQuizWrongIds();
      if (quizWrongIds.size > 0) {
        normalizedCatalog = normalizedCatalog.map((item) =>
          quizWrongIds.has(item.id)
            ? {
                ...item,
                is_mastered: false,
                is_review_later: true,
                status: "review_later" as const,
              }
            : item,
        );
      }
      const catalogIds = new Set(normalizedCatalog.map((i) => i.id));
      const fromApiPersonal = personalRes.personal_cards
        .filter((c) => !catalogIds.has(c.id))
        .map((c) => normalizeMmkItem(mmkItemFromPersonalCardApi(c)));
      const combined = [...normalizedCatalog, ...fromApiPersonal];
      const savedOrder = loadSavedOrder();
      let orderedItems =
        savedOrder && savedOrder.length > 0
          ? applySavedOrder(combined, savedOrder)
          : combined;
      saveOrder(orderedItems.map((i) => i.id));
      const firstCatalogItemId = orderedItems.find(
        (i) => i.source !== "personal",
      )?.id;
      const total = orderedItems.length;
      const mastered = orderedItems.filter((i) => i.is_mastered).length;
      saveMasteryCache(mastered, total);
      set({
        items: orderedItems,
        totalCount: total,
        lastKnownMasteredCount: mastered,
        lastKnownTotalCount: total,
      });

      if (firstCatalogItemId) {
        postMmkItemAction(firstCatalogItemId, "open_mmm")
          .then((response) => {
            if (generation !== fetchItemsGeneration) return;
            if (!response.item) return;
            const { items } = get();
            const quizWrongIds = loadQuizWrongIds();
            set({
              items: items.map((i) => {
                if (i.id !== response.item!.id) return i;
                const merged = { ...i, ...response.item };
                if (quizWrongIds.has(i.id)) {
                  merged.is_mastered = false;
                  merged.is_review_later = true;
                  merged.status = "review_later";
                }
                return normalizeMmkItem(merged);
              }),
            });
          })
          .catch(() => {});
      }
    } catch {
      if (generation !== fetchItemsGeneration) return;
      set({ error: "Failed to load MMM items", items: [] });
    } finally {
      if (generation === fetchItemsGeneration) {
        set({ loading: false });
      }
    }
  },

  clearStore: () => {
    fetchItemsGeneration += 1;
    set({
      items: [],
      totalCount: 15,
      loading: true,
      error: null,
      lastKnownMasteredCount: 0,
      lastKnownTotalCount: 15,
      isQuizInProgress: false,
      isFocusSessionInProgress: false,
    });
  },

  setItems: (items) => set({ items }),
  setTotalCount: (totalCount) => set({ totalCount }),

  updateItem: (id, updates) => {
    const quizWrongIds = loadQuizWrongIds();
    const nextItems = get().items.map((i) => {
      if (i.id !== id) return i;
      const merged = { ...i, ...updates };
      if (quizWrongIds.has(id)) {
        merged.is_mastered = false;
        merged.is_review_later = true;
        merged.status = "review_later";
      }
      return normalizeMmkItem(merged);
    });
    const mastered = nextItems.filter((i) => i.is_mastered).length;
    const total = get().totalCount;
    saveMasteryCache(mastered, total);
    set({
      items: nextItems,
      lastKnownMasteredCount: mastered,
      lastKnownTotalCount: total,
    });
    const scope = getScopeKey();
    if (scope) saveOrder(nextItems.map((i) => i.id));
  },

  addPersonalMmkItem: async (payload) => {
    const scope = getScopeKey();
    if (!scope) return { ok: false, error: "no_scope" };
    const personal = get().items.filter((i) => i.source === "personal");
    if (personal.length >= MAX_PERSONAL_MMK_CARDS) {
      return { ok: false, error: "limit" };
    }
    const q = payload.question.trim();
    const quick = payload.quickAnswer.trim();
    const full = payload.fullApprovedAnswer.trim();
    const newItem = normalizeMmkItem({
      id: crypto.randomUUID(),
      source: "personal",
      mkk_number: "",
      title: q,
      question: q,
      content: q,
      short_answer: quick,
      approved_answer: full,
      category: payload.category,
      is_mastered: false,
      is_pinned: false,
      is_review_later: false,
      status: "default",
    });
    const nextPersonal = [...personal, newItem];
    try {
      await savePersonalCards(personalMmkItemsToSavePayload(nextPersonal));
    } catch (e) {
      const st = getHttpStatus(e);
      if (st === 400) return { ok: false, error: "limit" };
      return { ok: false, error: "save_failed" };
    }
    const catalog = get().items.filter((i) => i.source !== "personal");
    const merged = [...catalog, ...nextPersonal];
    const saved = loadSavedOrder();
    const ordered =
      saved && saved.length > 0
        ? applySavedOrder(merged, saved)
        : merged;
    saveOrder(ordered.map((i) => i.id));
    const mastered = ordered.filter((i) => i.is_mastered).length;
    const total = ordered.length;
    saveMasteryCache(mastered, total);
    set({
      items: ordered,
      totalCount: total,
      lastKnownMasteredCount: mastered,
      lastKnownTotalCount: total,
    });
    return { ok: true };
  },

  editPersonalMmkItem: async (id, payload) => {
    const items = get().items;
    const target = items.find((i) => i.id === id && i.source === "personal");
    if (!target) return { ok: false, error: "not_found" };
    const q = payload.question.trim();
    const quick = payload.quickAnswer.trim();
    const full = payload.fullApprovedAnswer.trim();
    const newId = crypto.randomUUID();
    removeQuizWrongId(id);
    const replacement = normalizeMmkItem({
      ...target,
      id: newId,
      title: q,
      question: q,
      content: q,
      short_answer: quick,
      approved_answer: full,
      category: payload.category,
    });
    const personal = items
      .filter((i) => i.source === "personal")
      .map((i) => (i.id === id ? replacement : i));
    try {
      await savePersonalCards(personalMmkItemsToSavePayload(personal));
    } catch {
      return { ok: false, error: "save_failed" };
    }
    replaceSavedOrderId(id, newId);
    const catalog = items.filter((i) => i.source !== "personal");
    const merged = [...catalog, ...personal];
    const saved = loadSavedOrder();
    const ordered =
      saved && saved.length > 0 ? applySavedOrder(merged, saved) : merged;
    saveOrder(ordered.map((i) => i.id));
    const mastered = ordered.filter((i) => i.is_mastered).length;
    const total = ordered.length;
    saveMasteryCache(mastered, total);
    set({
      items: ordered,
      totalCount: total,
      lastKnownMasteredCount: mastered,
      lastKnownTotalCount: total,
    });
    return { ok: true, newId };
  },

  deletePersonalMmkItem: async (id) => {
    const items = get().items;
    const target = items.find((i) => i.id === id && i.source === "personal");
    if (!target) return { ok: false, error: "not_found" };
    removeQuizWrongId(id);
    const personal = items
      .filter((i) => i.source === "personal")
      .filter((i) => i.id !== id);
    try {
      await savePersonalCards(personalMmkItemsToSavePayload(personal));
    } catch {
      return { ok: false, error: "save_failed" };
    }
    removeIdFromSavedOrder(id);
    const catalog = items.filter((i) => i.source !== "personal");
    const merged = [...catalog, ...personal];
    const saved = loadSavedOrder();
    const ordered =
      saved && saved.length > 0 ? applySavedOrder(merged, saved) : merged;
    saveOrder(ordered.map((i) => i.id));
    const mastered = ordered.filter((i) => i.is_mastered).length;
    const total = ordered.length;
    saveMasteryCache(mastered, total);
    set({
      items: ordered,
      totalCount: total,
      lastKnownMasteredCount: mastered,
      lastKnownTotalCount: total,
    });
    return { ok: true };
  },

  addQuizWrongOverride: (id) => addQuizWrongId(id),
  removeQuizWrongOverride: (id) => removeQuizWrongId(id),
}));
